import { AxiosError, AxiosResponse } from 'axios'
import toast from 'react-hot-toast'
import { ERROR_CODES } from 'utils/constants'

const { INVALID_FILE_TYPE, LIMIT_FILE_SIZE } = ERROR_CODES

type UploadFileOptions = {
  chunkSizeInMb?: number
  setProgress?: React.Dispatch<React.SetStateAction<number>>
  onError?: () => void
  onComplete?: () => void
  toastId: any
}

/**
 * To Upload File in chunks
 * -
 * Three Parameters
 * 1. Axios Function
 * 2. file to be sent in chunks
 * 3. Options
 */
export const uploadFile = async (
  selectedFile: File | null,
  apiFunction: (data: any) => Promise<AxiosResponse>,
  {
    chunkSizeInMb = 5,
    setProgress,
    onError,
    onComplete,
    toastId,
  }: UploadFileOptions,
) => {
  // Size of chunk to be divided
  const chunkSize = Number(chunkSizeInMb) * 1024 * 1024

  // Will return error if no file is sent
  if (!selectedFile) {
    toast.error('Please select a file to upload.', {
      duration: 5000,
      id: toastId,
    })
    onError?.()
    return
  }

  // Total number of chunk generated
  const totalChunks = Math.ceil(selectedFile.size / chunkSize)

  // To maintain a prgress of successfull chunk sent
  const chunkProgress = 100 / totalChunks

  // Order of chunk
  let chunkNumber: number = 0

  let start: number = 0
  let end: number = 0

  // TO Upload next chunk
  const uploadNextChunk = async () => {
    start = end
    end = end + chunkSize
    chunkNumber += 1

    // To slice the file based on the given chunk size
    const chunk = selectedFile.slice(start, end)

    // Append chunk with all other detail in formdata
    const formData = new FormData()
    formData.append('file', chunk)
    formData.append('originalname', selectedFile.name)
    formData.append('chunkNumber', chunkNumber.toString())
    formData.append('totalChunks', totalChunks.toString())
    formData.append('fileSize', selectedFile.size.toString())

    // API call for sending buffer
    try {
      const response = await apiFunction(formData)
      // on successfully sending buffer
      if (response.status === 200) {
        setProgress?.(Math.round(chunkNumber * chunkProgress))

        // If not the last buffer upload next else complete
        if (chunkNumber < totalChunks) {
          uploadNextChunk()
        } else {
          onComplete?.()
        }
      } else {
        toast.error('Failed to upload chunk', {
          duration: 5000,
          id: toastId,
        })
        onError?.()
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const msg = error?.response?.data?.errors?.msg

        if (msg === INVALID_FILE_TYPE) {
          return toast.error('Invalid file type. Only CSV files are allowed.', {
            duration: 5000,
            id: toastId,
          })
        }
        if (msg?.code === LIMIT_FILE_SIZE) {
          return toast.error(`File size must be less than ${msg?.size}MB`, {
            duration: 5000,
            id: toastId,
          })
        }
      }
      toast.error('Error Uploading File', { duration: 5000, id: toastId })
      onError?.()
    }
  }

  // Used to upload the first chunk
  await uploadNextChunk()
}
